import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { buildPagedSortQueryParams } from '../utils/pagedSortQueryParams';
import { IVolunteerAttendance, isVolunteerBoardDisabledError, isVolunteerNotFoundError } from './models';

export const VOLUNTEER_ROSTER_KEY = '/volunteering/roster';

export const useVolunteerRosterQuery = ({
	activityId,
	pageSize = 50,
	enabled = true,
	impersonationContext,
}: {
	activityId?: string;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useInfiniteQuery({
		enabled: enabled && !!activityId,
		queryKey: [VOLUNTEER_ROSTER_KEY, activityId, pageSize, impersonationContext?.account?.id],
		queryFn: async ({ pageParam }) => {
			const value = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<IVolunteerAttendance>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `volunteering/activities/${activityId}/roster/filter`,
					queryParams: buildPagedSortQueryParams({ pageSize, pageToken: pageParam, expand: 'contact' }),
				}),
				'POST',
				{}
			);
			return value ?? { values: [] as IVolunteerAttendance[] };
		},
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		initialPageParam: '',
		meta: {
			toastErrorMessage: (error: Error) =>
				isVolunteerBoardDisabledError(error) || isVolunteerNotFoundError(error) ? '' : 'Failed to load the roster',
		},
	});
};

export const invalidateVolunteerRoster = (activityId?: string) =>
	reactQueryClient.invalidateQueries({
		queryKey: activityId ? [VOLUNTEER_ROSTER_KEY, activityId] : [VOLUNTEER_ROSTER_KEY],
	});
